import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import WhatsAppButton from "../components/site/WhatsAppButton";
import { ArrowUpRight, ArrowRight, ExternalLink, Loader2, ArrowLeft } from "lucide-react";

export default function PartnerPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    setData(null); setNotFound(false); setCat("All");
    api.get(`/partners/${slug}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [slug]);

  const goEnquire = (model) => navigate("/", { state: { scrollTo: "enquiry", partnerModel: model ? `${data.name} ${model}` : data.name } });

  if (notFound) return (
    <div className="grain min-h-screen"><Navbar /><div className="min-h-screen grid place-items-center">
      <div className="text-center"><div className="font-head font-black uppercase text-4xl text-white mb-4">Partner not found</div>
      <Link to="/partners" className="font-mono text-sm text-signal">← All partners</Link></div></div></div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center bg-ink"><Loader2 className="w-8 h-8 animate-spin text-signal" /></div>;

  const products = cat === "All" ? data.products : data.products.filter((p) => p.category === cat);
  const synced = data.last_synced ? new Date(data.last_synced).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;

  return (
    <div className="grain">
      <Navbar />
      <main className="pt-[72px]">
        {/* hero */}
        <section className="relative tech-grid border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
            <button onClick={() => navigate("/partners")} data-testid="pp-back-partners"
              className="font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-signal flex items-center gap-2 mb-8">
              <ArrowLeft className="w-4 h-4" /> All Partners
            </button>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-signal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-signal">Authorized Sourcing</span>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              className="font-head font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-white">
              {data.name}
            </motion.h1>
            <p className="mt-4 font-mono text-sm uppercase tracking-wider text-signal">{data.tagline}</p>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed max-w-3xl">{data.description}</p>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="partner-apps">
              {data.applications.map((a) => (
                <span key={a} className="font-mono text-[10px] uppercase tracking-wider border border-white/10 px-3 py-1.5 text-slate-400">{a}</span>
              ))}
            </div>
          </div>
        </section>

        {/* catalog */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-wrap gap-2 mb-10" data-testid="partner-filters">
            {data.categories.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`pf-${c.replace(/\s+/g, '-').toLowerCase()}`}
                className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 border transition-colors ${
                  cat === c ? "bg-signal text-ink border-signal" : "border-white/10 text-slate-400 hover:border-signal/50 hover:text-white"}`}>
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partner-grid">
            {products.map((p, i) => (
              <motion.div key={p.model + i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group bg-card border border-white/10 hover:border-signal/50 transition-colors overflow-hidden ticks flex flex-col"
                data-testid={`partner-product-${i}`}>
                <div className="relative aspect-[4/3] overflow-hidden bg-white/[0.03]">
                  <img src={p.image} alt={p.model} loading="lazy" referrerPolicy="no-referrer"
                    className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-ink/80 text-signal px-2 py-1 border border-signal/30">{p.series}</span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{p.category}</div>
                  <h3 className="font-head font-bold uppercase text-xl text-white leading-tight mb-3">{p.model}</h3>
                  <p className="text-sm text-slate-400 mb-4 flex-1">{p.description}</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {Object.entries(p.specs || {}).slice(0, 4).map(([k, v]) => (
                      <span key={k} className="font-mono text-[10px] text-slate-300 border border-white/10 px-2 py-1">
                        {k}: <span className="text-signal">{v}</span>
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => goEnquire(p.model)} data-testid={`partner-enquire-${i}`}
                      className="group/b flex-1 font-head font-bold uppercase tracking-wider text-sm px-4 py-3 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center justify-center gap-2">
                      Enquire <ArrowRight className="w-4 h-4 group-hover/b:translate-x-1 transition-transform" />
                    </button>
                    {p.link && (
                      <a href={p.link} target="_blank" rel="noopener noreferrer" data-testid={`partner-source-${i}`} title={`View on ${data.name}`}
                        className="w-11 h-11 grid place-items-center border border-white/15 text-slate-300 hover:border-signal hover:text-signal transition-colors shrink-0">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 font-mono text-[11px] text-slate-600 max-w-3xl leading-relaxed" data-testid="partner-disclaimer">
            Product data & imagery sourced from{" "}
            <a href={data.website} target="_blank" rel="noopener noreferrer" className="text-signal hover:underline">{data.website.replace(/^https?:\/\//, "")}</a>
            {synced && <> · catalog auto-synced {synced}</>}. Figures are indicative; full datasheets provided on enquiry.
            Brand names are trademarks of their respective owners — Dashmesh Enterprises is an authorized sourcing partner in India.
          </p>
        </section>

        <section className="border-t border-white/10 bg-ink-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-head font-black uppercase text-3xl sm:text-4xl text-white">Need help selecting a {data.name} model?</h2>
              <p className="text-slate-400 mt-2">Share your torque, ratio and joint requirements — we'll match the right component.</p>
            </div>
            <button onClick={() => goEnquire("")} data-testid="partner-cta"
              className="group font-head font-bold uppercase tracking-wider px-8 py-4 bg-signal text-ink hover:bg-signal-hover transition-colors flex items-center gap-2 whitespace-nowrap">
              Request a Quote <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}
